import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../packages/auth-client';
import { Spinner } from '../packages/ui';
import { acceptInvitation } from '../packages/api-client/pm-api';
import { getErrorMessage } from '../packages/api-client';
import { KaziAtlasLogo } from '../components/KaziAtlasLogo';
import { usePageTitle } from '../hooks/usePageTitle';
import { useTheme } from '../hooks/useTheme';

const POST_LOGIN_REDIRECT_KEY = 'postLoginRedirect';

export function InvitationAcceptPage() {
    usePageTitle('Accept invitation');
    const { theme } = useTheme();
    const { token = '' } = useParams<{ token: string }>();
    const { isAuthenticated, isLoading: authLoading } = useAuth();
    const navigate = useNavigate();

    const [status, setStatus] = useState<'pending' | 'error' | 'success'>('pending');
    const [errorMsg, setErrorMsg] = useState('');

    useEffect(() => {
        if (authLoading) return;
        if (!token) {
            setStatus('error');
            setErrorMsg('Missing invitation token.');
            return;
        }
        if (!isAuthenticated) {
            sessionStorage.setItem(POST_LOGIN_REDIRECT_KEY, `/invitations/${token}/accept`);
            navigate('/login', { replace: true });
            return;
        }

        let cancelled = false;
        (async () => {
            try {
                const { project_id } = await acceptInvitation(token);
                if (cancelled) return;
                setStatus('success');
                setTimeout(() => navigate(`/projects/${project_id}`, { replace: true }), 800);
            } catch (err) {
                if (cancelled) return;
                setStatus('error');
                setErrorMsg(getErrorMessage(err));
            }
        })();
        return () => { cancelled = true; };
    }, [authLoading, isAuthenticated, token, navigate]);

    return (
        <div className="login-container">
            <div className="login-card">
                <div style={{ marginBottom: '1rem' }}><KaziAtlasLogo size={46} onLight={theme === 'light'} /></div>

                {status === 'pending' && (
                    <>
                        <h1 className="login-title">Accepting your invitation…</h1>
                        <div style={{ display: 'flex', justifyContent: 'center', padding: '1.5rem 0' }}>
                            <Spinner size="lg" />
                        </div>
                    </>
                )}

                {status === 'success' && (
                    <>
                        <h1 className="login-title">You're in!</h1>
                        <p className="login-subtitle">Taking you to your new project…</p>
                    </>
                )}

                {status === 'error' && (
                    <>
                        <h1 className="login-title">Invitation couldn't be accepted</h1>
                        <p className="login-subtitle">{errorMsg || 'The invitation link is invalid or has expired.'}</p>
                        <button
                            type="button"
                            onClick={() => navigate('/dashboard', { replace: true })}
                            className="btn btn-primary"
                            style={{ marginTop: '1rem' }}
                        >
                            Go to dashboard
                        </button>
                    </>
                )}
            </div>
        </div>
    );
}
